import React, { useState, useRef, useEffect } from 'react';
import { theme } from '../../styles/theme';
import { audioController } from '../../utils/audioController';

interface AudioPreviewProps {
  previewUrl: string;
  trackTitle: string;
  onPlayStateChange?: (isPlaying: boolean) => void;
}

const AudioPreview: React.FC<AudioPreviewProps> = ({ previewUrl, trackTitle, onPlayStateChange }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(30); // iTunes previews are 30 seconds
  const audioRef = useRef<HTMLAudioElement>(null);
  const audioId = useRef<string>(`audio-${Math.random()}`); // Unique ID for this audio instance

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const updateTime = () => setCurrentTime(audio.currentTime);
    const updateDuration = () => setDuration(audio.duration || 30);
    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      onPlayStateChange?.(false);
      audioController.stop(audioId.current);
    };
    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange?.(false);
      audioController.stop(audioId.current);
    };

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('pause', handlePause);
    };
  }, [onPlayStateChange]);

  const togglePlay = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
      onPlayStateChange?.(false);
      audioController.stop(audioId.current);
    } else {
      try {
        // Stop any other playing audio first
        audioController.play(audio, audioId.current);
        await audio.play();
        setIsPlaying(true);
        onPlayStateChange?.(true);
      } catch (error) {
        console.error('Failed to play audio:', error);
      }
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * duration;
    
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const containerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '4px',
    borderRadius: theme.borderRadius.sm,
    backgroundColor: theme.colors.surfaceLight,
    border: `1px solid ${theme.colors.border}`,
    maxWidth: '200px',
  };

  const buttonStyle: React.CSSProperties = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: theme.colors.primary,
    color: 'white',
    fontSize: '12px',
    transition: `all ${theme.transitions.fast}`,
  };

  const progressContainerStyle: React.CSSProperties = {
    flex: 1,
    height: '4px',
    backgroundColor: theme.colors.surface,
    borderRadius: '2px',
    cursor: 'pointer',
    position: 'relative',
  };

  const progressBarStyle: React.CSSProperties = {
    height: '100%',
    backgroundColor: theme.colors.primary,
    borderRadius: '2px',
    width: `${(currentTime / duration) * 100}%`,
    transition: 'width 0.1s ease',
  };

  const timeStyle: React.CSSProperties = {
    fontSize: '0.7rem',
    color: theme.colors.textMuted,
    minWidth: '30px',
    textAlign: 'center',
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={containerStyle} title={`Preview: ${trackTitle}`}>
      <audio ref={audioRef} src={previewUrl} preload="metadata" />
      
      <button
        style={buttonStyle}
        onClick={togglePlay}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.primaryDark;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = theme.colors.primary;
        }}
      >
        {isPlaying ? '⏸' : '▶'}
      </button>
      
      <div style={progressContainerStyle} onClick={handleSeek}>
        <div style={progressBarStyle} />
      </div>
      
      <div style={timeStyle}>
        {formatTime(currentTime)}
      </div>
    </div>
  );
};

export default AudioPreview;